import { ReactElement, useState } from 'react';

import { Box, Flex, Grid, GridItem, Heading, Show, Text, Wrap, WrapItem } from '@chakra-ui/react';
import Underline from 'icons/underline.svg';
import AlexStasiak from 'images/AlexStasiak.png';
import MarekPalys from 'images/MarekPalys.png';
import { GetStaticProps } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';

import { HiddenSeoHeading } from '@/components/functional/HiddenSEOHeading/HiddenSEOHeading';
import { DefaultLayout, Footer } from '@/components/layouts';
import { ContactForm } from '@/components/sections/contact';
import { ContactPerson } from '@/components/sections/contact/ContactPerson/ContactPerson';
import { SectionContentWrapper, SectionWrapper } from '@/components/wrappers';
import { PropsBuilder } from '@/graphql/api';
import { useGetGlobalQuery, useGetMenuQuery } from '@/graphql/generated';
import { DEFAULT_LOCALE, DEFAULT_OG_IMAGE, LOCALES, PAGE_REVALIDATE_TIME } from '@/utils/constants';

import { NextPageWithLayout } from '../_app';

const CalendlyModalNoSSR = dynamic(() => import('../../components/molecules/CalendlyModal/CalendlyModal'), {
  ssr: false,
});

const CONTACT_PERSONS = [
  { name: 'Marek', role: 'CEO', src: MarekPalys, linkedin: 'https://pl.linkedin.com/in/marekpalys' },
  { name: 'Alex', role: 'Partner', src: AlexStasiak, linkedin: 'https://pl.linkedin.com/in/alexanderstasiak/en' },
];

const ContactPage: NextPageWithLayout = () => {
  const [calendlyOpen, setCalendlyOpen] = useState(false);
  const { t } = useTranslation([LOCALES.CONTACT]);

  return (
    <SectionWrapper backgroundColor="beige.50" overflow="hidden">
      <SectionContentWrapper
        display="flex"
        flexDir="column"
        alignItems="center"
        paddingY={undefined}
        paddingTop={{ base: '24px', md: '75px' }}
        paddingBottom={36}
        zIndex={0}
      >
        <HiddenSeoHeading heading="Contact" />
        <CalendlyModalNoSSR open={calendlyOpen} onClose={() => setCalendlyOpen(false)} />
        <Box w="100%" maxW="1440px">
          <Heading
            as="h1"
            fontSize={{
              base: '40px',
              md: '72px',
            }}
            fontWeight={{
              base: '400',
            }}
            lineHeight="120%"
            alignSelf="flex-start"
            color="purple.500"
            whiteSpace={{
              base: 'initial',
              sm: 'nowrap',
            }}
            mt={{
              base: '15px',
              md: '0',
            }}
            mb={{
              base: '16px',
              md: '18px',
            }}
          >
            {t('fillForm')}
          </Heading>
          <Grid
            templateColumns={{ base: '1fr', md: 'repeat(3,1fr)' }}
            templateRows={'1fr'}
            gap={{ base: 0, md: '62px' }}
          >
            <GridItem colStart={1} colEnd={3}>
              <ContactForm setCalendlyOpen={setCalendlyOpen} />
            </GridItem>
            <Show above="md">
              <GridItem>
                <Flex flexDir="column" height="100%" justify={'space-between'}>
                  <Box>
                    <Text variant="text-xs/lineHeight-4/font-medium">{t('mailContact')}</Text>
                    <Text variant="text-md/lineHeight-6/font-medium">
                      <a href="mailto:hello@start-up.house">hello@start-up.house</a>
                    </Text>
                    <Box as={Underline} w="199px" h="10px" position={'relative'} right="15px" />
                  </Box>
                  <Wrap spacing="20px">
                    {CONTACT_PERSONS.map((el) => (
                      <WrapItem key={el.role}>
                        <ContactPerson {...el} />
                      </WrapItem>
                    ))}
                  </Wrap>
                </Flex>
              </GridItem>
            </Show>
          </Grid>
        </Box>
      </SectionContentWrapper>
    </SectionWrapper>
  );
};

ContactPage.getLayout = (page: ReactElement) => {
  const { menu } = page.props;
  return (
    <DefaultLayout
      metadata={{
        metaTitle: "Let's get in touch!",
        metaDescription: 'Contact us form',
        ogImage: DEFAULT_OG_IMAGE,
      }}
      menu={menu}
    >
      {page}
      <Footer menu={menu} />
    </DefaultLayout>
  );
};

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const propsBuilder = new PropsBuilder();

  propsBuilder.withQuery(useGetGlobalQuery, {
    variables: {
      locale,
    },
  });
  propsBuilder.withQuery(useGetMenuQuery, {
    getMenu: (data) => data.menu,
  });

  const lang = await serverSideTranslations(locale || DEFAULT_LOCALE, [LOCALES.COMMON, LOCALES.CONTACT]);

  return {
    props: {
      ...(await propsBuilder.build(lang)),
    },
    revalidate: PAGE_REVALIDATE_TIME,
  };
};

export default ContactPage;
